<template>
  <div class="panel-section" v-loading="loading">
    <div class="section-header">
      <h3 class="section-title">{{ sectionTitle }}</h3>
      <span @click="handleMoreClick" class="more-link">
        {{ $t('overview.notice.moreLink') }}<i class="jc-icon-arrow-right"></i>
      </span>
    </div>
    <ul class="notice-list" v-if="normalizedNotices.length">
      <li
        v-for="(notice, index) in normalizedNotices"
        :key="index"
        class="notice-item"
        @click="handleItemClick(notice)"
      >
        <div class="notice-content">
          <span class="notice-tag">{{ notice.title }}</span>
        </div>
        <div class="notice-time">{{ notice.createTime }}</div>
      </li>
    </ul>
    <el-empty v-else :description="$t('text.noSearch')" :image-size="70"></el-empty>
  </div>
</template>

<script>
import { getNoticeConfig } from '@/components/notice/noticeConfig';

/**
 * NoticeList 侧边栏公告列表组件（UI 展示层）
 *
 * 架构说明：
 *   - 本组件为 UI 展示层，不直接感知具体公告类型的字段差异
 *   - 数据处理由 noticeConfig.js（数据处理层）完成，包含字段适配和接口映射
 *   - 路由跳转逻辑通过 emit 事件向父组件（SidePanel）暴露，父组件可自行处理
 *
 * @prop {String} noticeType - 公告类型标识，对应 noticeConfig.js 中的 NOTICE_TYPES key
 *                             默认为 'announcement'（普通公告）
 *
 * @event go-list   - 点击"更多"时触发，参数为 { noticeType }
 * @event go-detail - 点击公告项时触发，参数为 { noticeType, instId }
 */
export default {
  name: 'NoticeList',
  props: {
    // 公告类型标识，对应 noticeConfig.js 中的 NOTICE_TYPES key
    noticeType: {
      type: String,
      default: 'announcement',
    },
  },
  data() {
    return {
      loading: false,
      rawNotices: [],
    };
  },
  computed: {
    /**
     * 当前通知类型的配置对象（数据处理层）
     */
    config() {
      return getNoticeConfig(this.noticeType);
    },
    /**
     * 国际化标题
     */
    sectionTitle() {
      return this.$t(this.config.titleKey);
    },
    /**
     * 经过数据处理层适配后的统一格式列表
     * UI 层只消费 title / instId / createTime，与具体公告类型解耦
     */
    normalizedNotices() {
      return this.config.normalizeList(this.rawNotices);
    },
  },
  mounted() {
    this.fetchNotices();
  },
  methods: {
    /**
     * 获取公告列表
     * 通过配置的 listApiMethod 动态调用接口，结果由 extractListData 提取
     */
    fetchNotices() {
      const apiFn = this.$api[this.config.listApiMethod];
      if (typeof apiFn !== 'function') {
        console.error('[NoticeList] 接口方法不存在: ' + this.config.listApiMethod);
        return;
      }
      this.loading = true;
      apiFn({ pageSize: 5 })
        .then(res => {
          if (res.errno === 10000 && res.data) {
            const { list } = this.config.extractListData(res.data);
            this.rawNotices = list;
          }
        })
        .finally(() => {
          this.loading = false;
        });
    },
    /**
     * 点击"更多"：emit 事件，同时做默认路由跳转
     * 父组件可监听 go-list 事件以覆盖默认行为
     */
    handleMoreClick() {
      this.$emit('go-list', { noticeType: this.noticeType });
      this.$router.push({
        path: this.config.listRoute,
        query: { noticeType: this.noticeType },
      });
    },
    /**
     * 点击公告项：emit 事件，同时做默认路由跳转
     * 父组件可监听 go-detail 事件以覆盖默认行为
     */
    handleItemClick(notice) {
      this.$emit('go-detail', { noticeType: this.noticeType, instId: notice.instId });
      this.$router.push({
        path: this.config.detailRoute,
        query: { instId: notice.instId, noticeType: this.noticeType },
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.panel-section {
  background: #fff;
  padding: 24px;
  border-radius: 8px;

  .section-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 16px;

    .section-title {
      font-size: 14px;
      font-weight: 600;
      color: rgba(0, 0, 0, 0.85);
      margin: 0;
    }

    .more-link {
      font-size: 12px;
      color: rgba(0, 0, 0, 0.45);
      text-decoration: none;
      cursor: pointer;
      &:hover {
        color: #1D41FF;
      }
    }
  }

  // Notice List
  .notice-list {
    list-style: none;
    padding: 0;
    margin: 0;

    .notice-item {
      margin-bottom: 12px;
      font-size: 12px;
      cursor: pointer;
      transition: all 0.3s;

      &:last-child {
        margin-bottom: 0;
      }

      .notice-content {
        color: rgba(0, 0, 0, 0.65);
        margin-bottom: 4px;
        display: flex;
        align-items: center;

        .notice-tag {
          font-weight: bold;
        }
      }

      .notice-time {
        color: rgba(0, 0, 0, 0.45);
      }

      &:hover {
        .notice-content, .notice-time {
          color: #1D41FF;
        }
      }
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/components/resourceOverview/components/sidePanelComponents/NoticeList.vue